<template>
  <transition name="eb-modal">
    <div v-if="visible" class="eb-mask" @click.self="close">
      <div class="eb-modal" role="dialog" aria-modal="true" aria-label="绑定邮箱">
        <button class="eb-close" type="button" @click="close" aria-label="关闭">✕</button>

        <div class="eb-head">
          <div class="eb-title">绑定邮箱</div>
          <p class="eb-hint">
            绑定后即可用邮箱跨设备登录，并避免清理浏览器数据导致匿名身份丢失。当前账号会自动升级，
            已有的发帖、积分、点赞全部保留。
          </p>
        </div>

        <div class="eb-step">
          <label class="eb-label">邮箱</label>
          <input
            v-model.trim="email"
            class="eb-input"
            type="email"
            inputmode="email"
            placeholder="you@example.com"
            :disabled="loading"
            @keyup.enter="onSendCode"
          />
        </div>

        <div class="eb-step">
          <label class="eb-label">验证码</label>
          <div class="eb-code">
            <input
              v-model.trim="code"
              class="eb-input"
              type="text"
              inputmode="numeric"
              placeholder="请输入 6 位验证码"
              :disabled="loading"
              @keyup.enter="onBind"
            />
            <button
              class="btn-ghost eb-codebtn"
              type="button"
              :disabled="countdown > 0 || sending || loading"
              @click="onSendCode"
            >
              {{ countdown > 0 ? countdown + 's' : (sending ? '发送中…' : '发送验证码') }}
            </button>
          </div>
          <p v-if="debugHint" class="eb-debug">调试验证码已自动填入</p>
        </div>

        <button class="btn-primary eb-submit" type="button" :disabled="loading" @click="onBind">
          {{ loading ? '绑定中…' : '绑定邮箱' }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { useUserStore } from '@/store/user'
import { useUiStore } from '@/store/ui'
import { sendCode } from '@/api'

export default {
  name: 'EmailBindModal',
  props: {
    visible: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'success'],
  data() {
    return {
      userStore: useUserStore(),
      ui: useUiStore(),
      email: '',
      code: '',
      loading: false,
      sending: false,
      countdown: 0,
      debugHint: false,
      timer: null,
    }
  },
  watch: {
    // 关闭时清空表单，避免下次打开残留
    visible(v) {
      if (!v) this.reset()
    },
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    reset() {
      this.email = ''
      this.code = ''
      this.loading = false
      this.sending = false
      this.countdown = 0
      this.debugHint = false
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    },
    close() {
      this.$emit('update:visible', false)
    },
    startCountdown() {
      this.countdown = 60
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) { clearInterval(this.timer); this.timer = null }
      }, 1000)
    },
    async onSendCode() {
      if (!this.email) { this.ui.showToast('请输入邮箱', 'error'); return }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.email)) {
        this.ui.showToast('邮箱格式不正确', 'error'); return
      }
      if (this.sending || this.countdown > 0 || this.loading) return
      this.sending = true
      try {
        const res = await sendCode(this.email)
        this.startCountdown()
        this.ui.showToast('验证码已发送', 'success')
        if (res.data && res.data.debug_code) {
          this.code = res.data.debug_code
          this.debugHint = true
        }
      } catch (e) {
        this.ui.showToast(e.message || '发送失败', 'error')
      } finally {
        this.sending = false
      }
    },
    async onBind() {
      if (this.loading) return
      if (!this.email) { this.ui.showToast('请输入邮箱', 'error'); return }
      if (!this.code) { this.ui.showToast('请输入验证码', 'error'); return }
      this.loading = true
      try {
        // emailLogin 带 anon_key，后端会把当前匿名账号升级为邮箱账号
        await this.userStore.emailLogin(this.email, this.code)
        this.ui.showToast('邮箱绑定成功', 'success')
        this.$emit('success')
        this.close()
      } catch (e) {
        this.ui.showToast(e.message || '绑定失败', 'error')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.eb-mask {
  position: fixed; inset: 0; background: rgba(16,24,40,.45); z-index: 150;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 80px 16px; overflow-y: auto;
}
.eb-modal { position: relative; background: var(--surface); border-radius: var(--radius-lg); width: 100%; max-width: 420px; padding: 26px 24px 24px; box-shadow: var(--shadow-lg); }
.eb-close {
  position: absolute; top: 12px; right: 12px;
  width: 30px; height: 30px; border-radius: 8px; border: none; background: none;
  color: var(--text-3); font-size: 15px; cursor: pointer; transition: background .15s, color .15s;
}
.eb-close:hover { background: var(--surface-2); color: var(--text); }
.eb-title { font-size: 18px; font-weight: 800; margin-bottom: 8px; }
.eb-hint { font-size: 13px; color: var(--text-3); line-height: 1.7; margin-bottom: 18px; }
.eb-step { margin-bottom: 16px; }
.eb-label { display: block; font-size: 13px; font-weight: 600; color: var(--text-2); margin-bottom: 7px; }
.eb-input {
  width: 100%; height: 42px; padding: 0 13px;
  border: 1px solid var(--border-strong); border-radius: var(--radius-sm);
  font-size: 14px; color: var(--text); background: var(--surface); outline: none;
  transition: border-color .15s, box-shadow .15s; box-sizing: border-box;
}
.eb-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.eb-code { display: flex; gap: 10px; }
.eb-code .eb-input { flex: 1; }
.eb-codebtn { flex-shrink: 0; height: 42px; }
.eb-debug { font-size: 12px; color: var(--primary); margin-top: 8px; }
.eb-submit { width: 100%; height: 44px; justify-content: center; margin-top: 6px; }

.eb-modal-enter-active, .eb-modal-leave-active { transition: opacity .2s; }
.eb-modal-enter-from, .eb-modal-leave-to { opacity: 0; }
</style>
